import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { graphql } from 'babel-plugin-relay/macro';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment, useMutation } from 'relay-hooks';
import { CartRoutesQuery } from './__generated__/CartRoutesQuery.graphql';
import { CartRoutesCreateMutation } from './__generated__/CartRoutesCreateMutation.graphql';
import { CartRoutesUpdateMutation } from './__generated__/CartRoutesUpdateMutation.graphql';
import { CartRoutesDeleteMutation } from './__generated__/CartRoutesDeleteMutation.graphql';
import {
  CartBillingCustomerCreateMutation,
  CartBillingCustomerCreateMutationResponse,
} from './__generated__/CartBillingCustomerCreateMutation.graphql';
import Cart from './Cart';
import Shipping from './Shipping';
import Payment from './Payment';
import ReviewOrder from './ReviewOrder';
import Order from './Order';
import { Text } from '@resideo/blueprint-react';
import PageLoading from '../../../components/common/PageLoading';
import { CartContext } from '../CartProvider';
import PrintFriendlyView from './PrintFriendlyView';
import { useToast } from 'components/common/Toast';
import Alert from 'components/common/Alert';
import { useTranslation } from 'react-i18next';
import Page from 'components/common/Page';

interface MoneyProps {
  amount: number | string;
  currencyCode: string;
}

export const convertMoneyAmount = (value: MoneyProps, locale) => {
  if (
    value === null ||
    value === undefined ||
    locale === undefined ||
    !value?.currencyCode
  )
    return '';

  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: value?.currencyCode || 'USD',
  }).format(+value?.amount || 0);

  return money;
};

interface CartState {
  billingId?: string | null;
  defaultShipTo: any;
  defaultBillTo: any;
  partnerAccount: any;
  enrollments: any;
  viewer: any;
  isPartnerAccountIncomplete: boolean;
}

const CartRoutes: FC = () => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();
  const toast = useToast();
  const environment = useRelayEnvironment();

  const [cartRoutesState, setCartRoutesState] = useState<CartState>({
    billingId: null,
    defaultShipTo: null,
    defaultBillTo: null,
    partnerAccount: null,
    enrollments: null,
    viewer: null,
    isPartnerAccountIncomplete: true,
  });

  const viewerData = useCallback(() => {
    fetchQuery<CartRoutesQuery>(
      environment,
      graphql`
        query CartRoutesQuery {
          viewer {
            firstName
            lastName
            contactEmail
            primaryPhoneNumber
            phoneNumbers {
              number
            }
            partnerUsers {
              edges {
                node {
                  ... on PartnerUser {
                    id
                    partnerAccount {
                      ... on PartnerAccount {
                        contactEmail
                        primaryPhoneNumber
                        status
                        shipToAddress {
                          addressLine1
                          addressLine2
                          city
                          stateProvinceRegionCode
                          zipPostalCode
                          countryCode
                        }
                        partnerProgramEnrollments {
                          edges {
                            node {
                              id
                              status
                              partnerProgram {
                                name
                              }
                              ... on AirCyclePartnerProgramEnrollment {
                                variation
                              }
                            }
                          }
                        }
                        id
                        name
                        partnerBillings {
                          edges {
                            node {
                              ... on DigitalRiverBilling {
                                id
                                billingCustomerNumber
                                defaultShipTo {
                                  firstName
                                  lastName
                                  email
                                  organization
                                  address {
                                    addressLine1
                                    addressLine2
                                    city
                                    stateProvinceRegionCode
                                    zipPostalCode
                                    countryCode
                                    mailingAddressName
                                    contactName
                                  }
                                }
                                defaultBillTo {
                                  firstName
                                  lastName
                                  email
                                  organization
                                  address {
                                    addressLine1
                                    addressLine2
                                    city
                                    stateProvinceRegionCode
                                    zipPostalCode
                                    countryCode
                                    mailingAddressName
                                    contactName
                                  }
                                }
                                taxCertificate {
                                  companyName
                                  taxAuthority
                                  startDate
                                  endDate
                                }

                                paymentSources {
                                  paymentSourceId
                                  clientSecret
                                  state
                                  type
                                  paymentSessionId
                                  currency
                                  creditCard {
                                    lastFourDigits
                                    expirationMonth
                                    expirationYear
                                    brand
                                  }
                                  billTo {
                                    firstName
                                    lastName
                                    email
                                    organization
                                    address {
                                      addressLine1
                                      addressLine2
                                      city
                                      stateProvinceRegionCode
                                      zipPostalCode
                                      countryCode
                                      mailingAddressName
                                      contactName
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {}
    ).subscribe({
      next: data => {
        const theViewer = data?.viewer;
        const billingId =
          theViewer?.partnerUsers?.edges[0]?.node?.partnerAccount
            ?.partnerBillings?.edges[0]?.node?.id;
        const shipTo =
          theViewer?.partnerUsers?.edges[0]?.node?.partnerAccount
            ?.partnerBillings?.edges[0]?.node?.defaultShipTo;
        const billTo =
          theViewer?.partnerUsers?.edges[0]?.node?.partnerAccount
            ?.partnerBillings?.edges[0]?.node?.defaultBillTo;
        const defaultShipTo = {
          ...(shipTo
            ? {
                ...shipTo,
                ...(shipTo.address ? { address: { ...shipTo.address } } : null),
              }
            : null),
        };
        const defaultBillTo = {
          ...(billTo
            ? {
                ...billTo,
                ...(billTo.address ? { address: { ...billTo.address } } : null),
              }
            : null),
        };
        const partnerAccount =
          theViewer?.partnerUsers?.edges[0]?.node?.partnerAccount;
        const enrollments =
          partnerAccount?.partnerProgramEnrollments?.edges ?? [];

        const isPartnerAccountIncomplete = partnerAccount?.status !== 'ACTIVE';
        if (!isPartnerAccountIncomplete && !billingId) {
          createBillingCustomer[0]({
            variables: {
              partnerAccountId: `${partnerAccount?.id}`,
            },
            onError: err => {
              console.log(err);
              toast(
                <Alert variant='error'>
                  <Text>{t('cart.createBillingError')}</Text>
                </Alert>,
                { duration: 5000 }
              );
              setCartRoutesState({
                billingId: null,
                defaultShipTo: null,
                defaultBillTo: null,
                partnerAccount,
                enrollments,
                viewer: theViewer,
                isPartnerAccountIncomplete: isPartnerAccountIncomplete,
              });
            },
            onCompleted: (
              response: CartBillingCustomerCreateMutationResponse
            ) => {
              if (response.digitalRiverBillingCreate?.digitalRiverBilling) {
                const newBillingId =
                  response.digitalRiverBillingCreate.digitalRiverBilling.id;
                const shipTo =
                  response.digitalRiverBillingCreate.digitalRiverBilling
                    .defaultShipTo;
                const billTo =
                  response.digitalRiverBillingCreate.digitalRiverBilling
                    .defaultBillTo;
                const newDefaultShipTo = {
                  ...(shipTo
                    ? {
                        ...shipTo,
                        ...(shipTo.address
                          ? { address: { ...shipTo.address } }
                          : null),
                      }
                    : null),
                };
                const newDefaultBillTo = {
                  ...(billTo
                    ? {
                        ...billTo,
                        ...(billTo.address
                          ? { address: { ...billTo.address } }
                          : null),
                      }
                    : null),
                };

                setCartRoutesState({
                  billingId: newBillingId,
                  defaultShipTo: newDefaultShipTo,
                  defaultBillTo: newDefaultBillTo,
                  partnerAccount,
                  enrollments,
                  viewer: theViewer,
                  isPartnerAccountIncomplete,
                });
              } else {
                setCartRoutesState({
                  billingId: null,
                  defaultShipTo: null,
                  defaultBillTo: null,
                  partnerAccount,
                  enrollments,
                  viewer: theViewer,
                  isPartnerAccountIncomplete: isPartnerAccountIncomplete,
                });
              }
            },
          });
        } else {
          setCartRoutesState({
            billingId,
            defaultShipTo,
            defaultBillTo,
            partnerAccount,
            enrollments,
            viewer: theViewer,
            isPartnerAccountIncomplete,
          });
        }
      },
      error: () => {
        setCartRoutesState({
          billingId: null,
          defaultShipTo: null,
          defaultBillTo: null,
          partnerAccount: null,
          enrollments: null,
          viewer: null,
          isPartnerAccountIncomplete: true,
        });
      },
    });
  }, [environment]);

  const deleteCheckoutMutation = useMutation<CartRoutesDeleteMutation>(graphql`
    mutation CartRoutesDeleteMutation(
      $checkoutId: ID!
      $partnerAccountId: ID!
    ) {
      digitalRiverCheckoutDelete(
        digitalRiverCheckoutId: $checkoutId
        partnerAccountId: $partnerAccountId
      ) {
        errors {
          message
          field
        }
      }
    }
  `);

  const createCheckoutMutation = useMutation<CartRoutesCreateMutation>(graphql`
    mutation CartRoutesCreateMutation(
      $input: DigitalRiverCheckoutCreateInput!
      $digitalRiverBillingId: ID!
    ) {
      digitalRiverCheckoutCreate(
        input: $input
        digitalRiverBillingId: $digitalRiverBillingId
      ) {
        errors {
          message
          field
        }
        digitalRiverCheckout {
          id
          createdAt
          updatedAt
          shipTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          billTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          currency
          lineItems {
            lineItemId
            sku
            quantity
            amount {
              amount
              currencyCode
            }
          }
          paymentSource {
            paymentSourceId
            paymentSessionId
            clientSecret
            state
            currency
            type
            creditCard {
              lastFourDigits
              expirationMonth
              expirationYear
              brand
            }
            payPalBillingToken
          }
          shippingChoice {
            price {
              amount
              currencyCode
            }
            serviceLevel
          }
          taxRate
          totalShipping {
            amount
            currencyCode
          }
          subTotal {
            amount
            currencyCode
          }
          tax {
            amount
            currencyCode
          }
          total {
            amount
            currencyCode
          }
        }
      }
    }
  `);

  const updateCheckoutMutation = useMutation<CartRoutesUpdateMutation>(
    graphql`
      mutation CartRoutesUpdateMutation(
        $digitalRiverBillingId: ID!
        $checkoutId: ID!
        $input: DigitalRiverCheckoutUpdateInput!
      ) {
        digitalRiverCheckoutUpdate(
          digitalRiverBillingId: $digitalRiverBillingId
          digitalRiverCheckoutId: $checkoutId
          input: $input
        ) {
          errors {
            message
            field
          }
          digitalRiverCheckout {
            id
            createdAt
            updatedAt
            shipTo {
              firstName
              lastName
              email
              organization
              address {
                addressLine1
                addressLine2
                city
                stateProvinceRegionCode
                zipPostalCode
                countryCode
                mailingAddressName
                contactName
              }
            }
            billTo {
              firstName
              lastName
              email
              organization
              address {
                addressLine1
                addressLine2
                city
                stateProvinceRegionCode
                zipPostalCode
                countryCode
                mailingAddressName
                contactName
              }
            }
            currency
            lineItems {
              sku
              quantity
              amount {
                amount
                currencyCode
              }
            }
            paymentSource {
              paymentSourceId
              paymentSessionId
              clientSecret
              state
              currency
              type
            }
            shippingChoice {
              price {
                amount
                currencyCode
              }
              serviceLevel
            }
            taxRate
            totalShipping {
              amount
              currencyCode
            }
            subTotal {
              amount
              currencyCode
            }
            tax {
              amount
              currencyCode
            }
            total {
              amount
              currencyCode
            }
          }
        }
      }
    `
  );

  const createOrderMutation = useMutation(graphql`
    mutation CartRoutesOrderCreateMutation(
      $checkoutId: ID!
      $partnerAccountId: ID!
    ) {
      completePartnerProgramEnrollment(
        digitalRiverCheckoutId: $checkoutId
        partnerAccountId: $partnerAccountId
      ) {
        errors {
          message
          field
        }
        digitalRiverOrder {
          id
          createdAt
          updatedAt
          invoiceNumber
          shipTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          billTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          currency
          paymentSource {
            paymentSessionId
            clientSecret
            state
            currency
            type
          }
          lineItems {
            sku
            quantity
            amount {
              amount
              currencyCode
            }
            billingPeriodStartDate
            billingPeriodEndDate
            deviceNumber
          }
          shippingChoice {
            price {
              amount
              currencyCode
            }
            serviceLevel
          }
          totalShipping {
            amount
            currencyCode
          }
          subTotal {
            amount
            currencyCode
          }
          tax {
            amount
            currencyCode
          }
          total {
            amount
            currencyCode
          }
        }
      }
    }
  `);

  const createBillingCustomer = useMutation<
    CartBillingCustomerCreateMutation
  >(graphql`
    mutation CartBillingCustomerCreateMutation($partnerAccountId: ID!) {
      digitalRiverBillingCreate(partnerAccountId: $partnerAccountId) {
        errors {
          message
          field
        }
        digitalRiverBilling {
          id
          billingCustomerNumber
          defaultShipTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          defaultBillTo {
            firstName
            lastName
            email
            organization
            address {
              addressLine1
              addressLine2
              city
              stateProvinceRegionCode
              zipPostalCode
              countryCode
              mailingAddressName
              contactName
            }
          }
          taxCertificate {
            companyName
            taxAuthority
            startDate
            endDate
          }
          paymentSources {
            paymentSourceId
            clientSecret
            state
            type
            paymentSessionId
            currency
            creditCard {
              lastFourDigits
              expirationMonth
              expirationYear
              brand
            }
          }
        }
      }
    }
  `);

  const { state } = useContext(CartContext);

  useEffect(() => {
    viewerData();
  }, []);

  const showOnboardingState = cartRoutesState.isPartnerAccountIncomplete;
  return !cartRoutesState.viewer ? (
    <PageLoading />
  ) : (
    <Page as='main' maxWidth='1375px' data-test-cart-wrapper>
      {state.isFetching && <PageLoading />}
      <Switch>
        <Route
          exact
          path={path}
          component={() => (
            <Cart
              showOnboardingState={showOnboardingState}
              userPlans={cartRoutesState.enrollments}
              digitalRiverBillingId={cartRoutesState.billingId}
              partnerAccountId={cartRoutesState.partnerAccount?.id}
              createCheckoutMutation={createCheckoutMutation[0]}
              deleteCheckoutMutation={deleteCheckoutMutation[0]}
            />
          )}
        />
        <Route
          path={`${path}/shipping`}
          component={() => (
            <Shipping
              // partnerAccountId={partnerAccountId}
              digitalRiverBillingId={cartRoutesState.billingId}
              updateCheckoutMutation={updateCheckoutMutation[0]}
              defaultShipTo={cartRoutesState.defaultShipTo}
              defaultBillTo={cartRoutesState.defaultBillTo}
              viewerData={cartRoutesState.viewer}
            />
          )}
        />
        <Route
          path={`${path}/payment`}
          component={() => (
            <Payment
              digitalRiverBillingId={cartRoutesState.billingId}
              updateCheckoutMutation={updateCheckoutMutation[0]}
              defaultBillTo={cartRoutesState.defaultBillTo}
              viewer={cartRoutesState.viewer}
            />
          )}
        />
        <Route
          path={`${path}/review-order`}
          component={() => (
            <ReviewOrder
              data-test-review-order-page
              createOrderMutation={createOrderMutation[0]}
              updateCheckoutMutation={updateCheckoutMutation[0]}
              viewerData={cartRoutesState.viewer}
              digitalRiverBillingId={cartRoutesState.billingId}
            />
          )}
        />
        <Route
          path={`${path}/order/:id`}
          component={() => <Order viewerData={cartRoutesState.viewer} />}
        />
        <Route
          path={`${path}/print-friendly-view/:id`}
          component={() => (
            <PrintFriendlyView viewerData={cartRoutesState.viewer} />
          )}
        />
      </Switch>
    </Page>
  );
};

export default CartRoutes;
